import { useState, useEffect, useMemo } from 'react'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import SearchBar from '../components/SearchBar'
import PostnummerFilter from '../components/PostnummerFilter'
import DateFilterBar, { OutputMode, getDateRangeForMode, formatNorwegianDate } from '../components/DateFilterBar'
import ExportButtons from '../components/ExportButtons'
import { TopBrandsChart } from '../components/Charts'
import PeriodInfoBanner from '../components/PeriodInfoBanner'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { ChevronRight, ArrowLeft, TrendingUp, Package, Zap, Building2, Car, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { useYearDates } from '../contexts/YearContext'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { ColumnDefinition } from '../utils/exportToCsv'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useMineMerker } from '../hooks/useMineMerker'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'

// Helper function to safely parse numeric values that might be strings
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

// Brand Stats interface (from /public/top-brands endpoint)
interface BrandStats {
  ranking: number | null // null for summary row (TOTAL), 1-N for brands
  brand: string // "TOTAL" for summary row
  total_count: number
  market_share_percentage: number // 100.0 for summary row
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  undefined_leasing_count: number
  undefined_leasing_percentage: number
  company_sales_count: number
  company_sales_percentage: number
  avg_owner_age: number | null
}

// Master Model interface (from /public/master-models endpoint)
interface MasterModelStats {
  ranking: number | null // null for summary row, 1-N for models
  master_model_id: number | null // null for summary row
  brand_key: string | null // null for summary row
  brand: string // "TOTAL" for summary row
  master_model: string // "All Models" for summary row
  segment_id: number | null
  body_type: string | null
  total_count: number
  market_share_percentage: number // 100.0 for summary row
  is_electric: 0 | 1
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  undefined_leasing_count: number
  undefined_leasing_percentage: number
  company_sales_count: number
  company_sales_percentage: number
  avg_owner_age: number | null
}

// Model Variant interface (for future use when backend endpoint is ready)
interface ModelVariantStats {
  model_variant: string
  brand: string
  total_count: number
  market_share_percentage: number
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  undefined_leasing_count: number
  undefined_leasing_percentage: number
  company_sales_count: number
  company_sales_percentage: number
  avg_owner_age: number | null
}

export default function BrandAnalytics() {
  const { t } = useTranslation('analytics')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [brands, setBrands] = useState<BrandStats[]>([])
  const [filteredBrands, setFilteredBrands] = useState<BrandStats[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [brandSummary, setBrandSummary] = useState<BrandStats | null>(null)
  
  // Date range state from year context
  const { startDate, setStartDate, endDate, setEndDate, selectedYear, maxSvvDate } = useYearDates('brand-analytics')
  
  // Vehicle filter
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()
  
  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('brand-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)
  const { activeMerker } = useMineMerker()

  // Date filter state
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')
  const [dateRangeAlert, setDateRangeAlert] = useState<string | null>(null)
  const maxDataDate = useMaxDataDate()
  
  // Sort state
  const [brandSortConfig, setBrandSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [masterModelSortConfig, setMasterModelSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [variantSortConfig, setVariantSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  
  // Level 2: Master Models view state (when brand is selected)
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null)
  const [_selectedBrandKey, setSelectedBrandKey] = useState<string | null>(null) // Stored for future API use when backend endpoint is ready
  const [masterModels, setMasterModels] = useState<MasterModelStats[]>([])
  const [masterModelsSummary, setMasterModelsSummary] = useState<MasterModelStats | null>(null)
  const [masterModelsLoading, setMasterModelsLoading] = useState(false)
  const [masterModelsError, setMasterModelsError] = useState<string | null>(null)
  
  // Level 3: Model Variants view state (when master model is selected)
  const [selectedMasterModel, setSelectedMasterModel] = useState<MasterModelStats | null>(null)
  const [modelVariants, setModelVariants] = useState<ModelVariantStats[]>([])
  const [variantsLoading, setVariantsLoading] = useState(false)
  const [variantsError, setVariantsError] = useState<string | null>(null)

  useEffect(() => {
    fetchBrands()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, vehicleFilter]) // Fetch when year or vehicle filter changes

  // Validate date range (max 366 days)
  const validateDateRange = (start: Date, end: Date): boolean => {
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays > 366) {
      setDateRangeAlert(t('common.dateRangeTooLarge', { days: diffDays }))
      return false
    }
    
    setDateRangeAlert(null)
    return true
  }

  const handleOutputModeChange = (mode: OutputMode) => {
    setOutputMode(mode)
    if (mode !== 'custom') {
      const range = getDateRangeForMode(mode, maxSvvDate)
      const s = new Date(range.startDate)
      const e = new Date(range.endDate)
      setStartDate(s)
      setEndDate(e)
      setDateRangeAlert(null)
      setTimeout(() => fetchBrands(), 0)
    }
  }

  const handleApplyFilters = () => {
    if (outputMode === 'custom') {
      if (!customStartDate || !customEndDate) return
      const s = new Date(customStartDate)
      const e = new Date(customEndDate)
      if (!validateDateRange(s, e)) return
      setStartDate(s)
      setEndDate(e)
      // Need to fetch after state update
      setTimeout(() => fetchBrands(), 0)
    } else {
      const range = getDateRangeForMode(outputMode, maxSvvDate)
      const s = new Date(range.startDate)
      const e = new Date(range.endDate)
      setStartDate(s)
      setEndDate(e)
      setDateRangeAlert(null)
      setTimeout(() => fetchBrands(), 0)
    }
  }


  useEffect(() => {
    const filtered = brands.filter(brand =>
      brand.brand?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    setFilteredBrands(filtered)
  }, [brands, searchTerm])

  const fetchBrands = async () => {
    try {
      setLoading(true)
      setError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date, limit: 100 }
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }
      
      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await publicAPI.getTopBrands(params)
      const data = response.data.data || []
      
      // First item is the summary row (ranking === null)
      const [summary, ...brandRows] = data
      setBrandSummary(summary || null)
      setBrands(brandRows)
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  // Sort handler
  const handleSort = (key: string) => {
    if (brandSortConfig?.key === key) {
      // Toggle: asc -> desc -> null (clear)
      if (brandSortConfig.direction === 'asc') {
        setBrandSortConfig({ key, direction: 'desc' })
      } else {
        setBrandSortConfig(null)
      }
    } else {
      // New column, start with ascending
      setBrandSortConfig({ key, direction: 'asc' })
    }
  }

  // Get sort icon for column header
  const getSortIcon = (columnKey: string) => {
    if (brandSortConfig?.key === columnKey) {
      return brandSortConfig.direction === 'asc' ? 
        <ArrowUp className="w-4 h-4 inline ml-1" /> : 
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // Sorted brand data
  const sortedBrands = useMemo(() => {
    if (!brandSortConfig) return filteredBrands

    const sorted = [...filteredBrands].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (brandSortConfig.key) {
        case 'ranking':
          aVal = a.ranking !== null ? a.ranking : Infinity
          bVal = b.ranking !== null ? b.ranking : Infinity
          break
        case 'brand':
          aVal = a.brand.toLowerCase()
          bVal = b.brand.toLowerCase()
          break
        case 'total_count':
          aVal = toNumber(a.total_count)
          bVal = toNumber(b.total_count)
          break
        case 'market_share_percentage':
          aVal = toNumber(a.market_share_percentage)
          bVal = toNumber(b.market_share_percentage)
          break
        case 'avg_owner_age':
          aVal = a.avg_owner_age !== null ? toNumber(a.avg_owner_age) : -Infinity
          bVal = b.avg_owner_age !== null ? toNumber(b.avg_owner_age) : -Infinity
          break
        case 'leasing_percentage':
          aVal = toNumber(a.leasing_percentage)
          bVal = toNumber(b.leasing_percentage)
          break
        case 'company_sales_percentage':
          aVal = toNumber(a.company_sales_percentage)
          bVal = toNumber(b.company_sales_percentage)
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return brandSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return brandSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [filteredBrands, brandSortConfig])

  // Column definitions for export
  const brandColumns: ColumnDefinition[] = [
    { key: 'ranking', label: 'Ranking' },
    { key: 'brand', label: t('common.brand') },
    { 
      key: 'total_count', 
      label: t('common.registrations'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'market_share_percentage', 
      label: t('common.marketSharePct'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    { 
      key: 'avg_owner_age', 
      label: t('common.avgOwnerAge'),
      format: (val) => val ? toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) : '-'
    },
    { 
      key: 'leasing_percentage', 
      label: 'Leasing (%)',
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
    { 
      key: 'company_sales_percentage', 
      label: t('common.companySalesPctExport'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
  ]

  // Master Model sort handler
  const handleMasterModelSort = (key: string) => {
    if (masterModelSortConfig?.key === key) {
      if (masterModelSortConfig.direction === 'asc') {
        setMasterModelSortConfig({ key, direction: 'desc' })
      } else {
        setMasterModelSortConfig(null)
      }
    } else {
      setMasterModelSortConfig({ key, direction: 'asc' })
    }
  }

  // Get master model sort icon
  const getMasterModelSortIcon = (columnKey: string) => {
    if (masterModelSortConfig?.key === columnKey) {
      return masterModelSortConfig.direction === 'asc' ? 
        <ArrowUp className="w-4 h-4 inline ml-1" /> : 
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // Sorted master model data
  const sortedMasterModels = useMemo(() => {
    if (!masterModelSortConfig) return masterModels

    const sorted = [...masterModels].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (masterModelSortConfig.key) {
        case 'ranking':
          aVal = a.ranking !== null ? a.ranking : Infinity
          bVal = b.ranking !== null ? b.ranking : Infinity
          break
        case 'master_model':
          aVal = a.master_model.toLowerCase()
          bVal = b.master_model.toLowerCase()
          break
        case 'total_count':
          aVal = toNumber(a.total_count)
          bVal = toNumber(b.total_count)
          break
        case 'market_share_percentage':
          aVal = toNumber(a.market_share_percentage)
          bVal = toNumber(b.market_share_percentage)
          break
        case 'ev_percentage':
          aVal = toNumber(a.ev_percentage)
          bVal = toNumber(b.ev_percentage)
          break
        case 'leasing_percentage':
          aVal = toNumber(a.leasing_percentage)
          bVal = toNumber(b.leasing_percentage)
          break
        case 'company_total':
          aVal = toNumber(a.company_sales_count) + toNumber(a.company_leasing_count)
          bVal = toNumber(b.company_sales_count) + toNumber(b.company_leasing_count)
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return masterModelSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return masterModelSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [masterModels, masterModelSortConfig])

  // Master model column definitions for export
  const masterModelColumns: ColumnDefinition[] = [
    { key: 'ranking', label: 'Ranking' },
    { key: 'master_model', label: t('common.masterModel') },
    { key: 'body_type', label: 'Type' },
    { 
      key: 'total_count', 
      label: t('common.registrations'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'market_share_percentage', 
      label: t('common.marketSharePct'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    { 
      key: 'ev_percentage', 
      label: t('common.evPctExport'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
    { 
      key: 'leasing_percentage', 
      label: 'Leasing (%)',
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
    { 
      key: 'company_total', 
      label: t('brand.companyTotalCount'),
      format: (_val, row: any) => (toNumber(row.company_sales_count) + toNumber(row.company_leasing_count)).toLocaleString('nb-NO')
    },
  ]

  // Variant sort handler
  const handleVariantSort = (key: string) => {
    if (variantSortConfig?.key === key) {
      if (variantSortConfig.direction === 'asc') {
        setVariantSortConfig({ key, direction: 'desc' })
      } else {
        setVariantSortConfig(null)
      }
    } else {
      setVariantSortConfig({ key, direction: 'asc' })
    }
  }

  // Get variant sort icon
  const getVariantSortIcon = (columnKey: string) => {
    if (variantSortConfig?.key === columnKey) {
      return variantSortConfig.direction === 'asc' ? 
        <ArrowUp className="w-4 h-4 inline ml-1" /> : 
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // Sorted variant data
  const sortedVariants = useMemo(() => {
    if (!variantSortConfig) return modelVariants

    const sorted = [...modelVariants].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (variantSortConfig.key) {
        case 'model_variant':
          aVal = a.model_variant.toLowerCase()
          bVal = b.model_variant.toLowerCase()
          break
        case 'total_count':
          aVal = toNumber(a.total_count)
          bVal = toNumber(b.total_count)
          break
        case 'market_share_percentage':
          aVal = toNumber(a.market_share_percentage)
          bVal = toNumber(b.market_share_percentage)
          break
        case 'ev_percentage':
          aVal = toNumber(a.ev_percentage)
          bVal = toNumber(b.ev_percentage)
          break
        case 'leasing_percentage':
          aVal = toNumber(a.leasing_percentage)
          bVal = toNumber(b.leasing_percentage)
          break
        case 'company_percentage':
          aVal = toNumber(a.company_sales_percentage) + toNumber(a.company_leasing_percentage)
          bVal = toNumber(b.company_sales_percentage) + toNumber(b.company_leasing_percentage)
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return variantSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return variantSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [modelVariants, variantSortConfig])

  // Variant column definitions for export
  const variantColumns: ColumnDefinition[] = [
    { key: 'model_variant', label: 'Variant' },
    { 
      key: 'total_count', 
      label: t('common.registrations'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'market_share_percentage', 
      label: t('common.marketSharePct'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    { 
      key: 'ev_percentage', 
      label: t('common.evPctExport'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
    { 
      key: 'leasing_percentage', 
      label: 'Leasing (%)',
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
    { 
      key: 'company_percentage', 
      label: t('common.companyPctExport'),
      format: (_val, row: any) => (toNumber(row.company_sales_percentage) + toNumber(row.company_leasing_percentage)).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
  ]

  // Fetch master models for a selected brand
  const fetchBrandMasterModels = async (brandName: string) => {
    try {
      setMasterModelsLoading(true)
      setMasterModelsError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date, limit: 200 }
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }
      
      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      // Fetch all master models and filter by brand name client-side
      const response = await publicAPI.getMasterModels(params)
      const allMasterModels = response.data.data || []
      
      // Filter out summary row (ranking === null) and filter by brand name (case-insensitive)
      const brandMasterModels = allMasterModels.filter(
        (mm: MasterModelStats) => mm.ranking !== null && mm.brand.toLowerCase() === brandName.toLowerCase()
      )
      
      // Create a summary specific to this brand
      if (brandMasterModels.length > 0) {
        const totalCount = brandMasterModels.reduce((sum: number, mm: MasterModelStats) => sum + mm.total_count, 0)
        const evCount = brandMasterModels.reduce((sum: number, mm: MasterModelStats) => sum + mm.ev_count, 0)
        const leasingCount = brandMasterModels.reduce((sum: number, mm: MasterModelStats) => sum + mm.leasing_count, 0)
        const privateLeasingCount = brandMasterModels.reduce((sum: number, mm: MasterModelStats) => sum + mm.private_leasing_count, 0)
        const companyLeasingCount = brandMasterModels.reduce((sum: number, mm: MasterModelStats) => sum + mm.company_leasing_count, 0)
        const undefinedLeasingCount = brandMasterModels.reduce((sum: number, mm: MasterModelStats) => sum + mm.undefined_leasing_count, 0)
        const companySalesCount = brandMasterModels.reduce((sum: number, mm: MasterModelStats) => sum + mm.company_sales_count, 0)
        
        const brandSpecificSummary: MasterModelStats = {
          ranking: null,
          master_model_id: null,
          brand_key: null,
          brand: brandName,
          master_model: 'TOTAL',
          segment_id: null,
          body_type: null,
          total_count: totalCount,
          market_share_percentage: totalCount > 0 
            ? brandMasterModels.reduce((sum: number, mm: MasterModelStats) => sum + (mm.market_share_percentage * mm.total_count), 0) / totalCount
            : 0,
          is_electric: 0,
          ev_count: evCount,
          ev_percentage: totalCount > 0 ? (evCount / totalCount) * 100 : 0,
          leasing_count: leasingCount,
          leasing_percentage: totalCount > 0 ? (leasingCount / totalCount) * 100 : 0,
          private_leasing_count: privateLeasingCount,
          private_leasing_percentage: totalCount > 0 ? (privateLeasingCount / totalCount) * 100 : 0,
          company_leasing_count: companyLeasingCount,
          company_leasing_percentage: totalCount > 0 ? (companyLeasingCount / totalCount) * 100 : 0,
          undefined_leasing_count: undefinedLeasingCount,
          undefined_leasing_percentage: totalCount > 0 ? (undefinedLeasingCount / totalCount) * 100 : 0,
          company_sales_count: companySalesCount,
          company_sales_percentage: totalCount > 0 ? (companySalesCount / totalCount) * 100 : 0,
          avg_owner_age: null
        }
        setMasterModelsSummary(brandSpecificSummary)
      }
      
      // Store brand_key for future use
      if (brandMasterModels.length > 0) {
        setSelectedBrandKey(brandMasterModels[0].brand_key)
      }
      
      setMasterModels(brandMasterModels)
    } catch (err: any) {
      console.error('Master models API error:', err)
      setMasterModelsError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadMasterModelsFailed'))
    } finally {
      setMasterModelsLoading(false)
    }
  }

  // Fetch model variants for a selected master model
  const fetchMasterModelVariants = async (masterModel: MasterModelStats) => {
    try {
      setVariantsLoading(true)
      setVariantsError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date, limit: 100 }
      
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }
      
      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await publicAPI.getMasterModelVariants(masterModel.master_model_id!, params)
      setModelVariants(response.data.data || [])
    } catch (err: any) {
      console.error('Model variants API error:', err)
      setVariantsError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadVariantsFailed'))
    } finally {
      setVariantsLoading(false)
    }
  }

  // Level 1 → Level 2: User clicks a brand to see master models
  const handleBrandClick = (brandName: string) => {
    setSelectedBrand(brandName)
    setSelectedMasterModel(null)
    setModelVariants([])
    fetchBrandMasterModels(brandName)
  }

  // Level 2 → Level 3: User clicks a master model to see variants
  const handleMasterModelClick = (masterModel: MasterModelStats) => {
    setSelectedMasterModel(masterModel)
    fetchMasterModelVariants(masterModel)
  }

  // Back from Level 2 to Level 1
  const handleBackToList = () => {
    setSelectedBrand(null)
    setSelectedBrandKey(null)
    setMasterModels([])
    setMasterModelsError(null)
    setSelectedMasterModel(null)
    setModelVariants([])
    setVariantsError(null)
  }

  // Back from Level 3 to Level 2
  const handleBackToMasterModels = () => {
    setSelectedMasterModel(null)
    setModelVariants([])
    setVariantsError(null)
  }

  // Calculate total company vehicles for a master model
  const getTotalCompanyVehicles = (model: MasterModelStats) => {
    return model.company_leasing_count + model.company_sales_count
  }

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  // Level 3: Model Variants view (when master model is selected)
  if (selectedBrand && selectedMasterModel) {
    return (
      <div className="space-y-6">
        {/* Header with breadcrumb navigation */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToMasterModels}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-dark-card hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('common.backToMasterModels')}</span>
          </button>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <span className="hover:text-primary-600 cursor-pointer" onClick={handleBackToList}>{t('brand.brands')}</span>
              <ChevronRight className="w-3 h-3" />
              <span className="hover:text-primary-600 cursor-pointer" onClick={handleBackToMasterModels}>{selectedBrand}</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-900 dark:text-gray-100 font-medium">{selectedMasterModel.master_model}</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {t('common.variantsTitle', { brand: selectedBrand, model: selectedMasterModel.master_model })}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {t('common.modelVariantsFor', { model: selectedMasterModel.master_model })}
            </p>
          </div>
        </div>

        {/* Master Model Summary */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <Car className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.totalRegistrations')}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedMasterModel.total_count.toLocaleString('nb-NO')}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.evShare')}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedMasterModel.ev_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.marketShare')}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedMasterModel.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <Package className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Leasing %</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedMasterModel.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <Building2 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.companyTotal')}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {getTotalCompanyVehicles(selectedMasterModel).toLocaleString('nb-NO')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Variants Table */}
        {variantsLoading ? (
          <LoadingSpinner />
        ) : variantsError ? (
          <div className="card p-8">
            <div className="text-center">
              <Car className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t('common.variantsComingSoon')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('common.variantsEndpointWip')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {t('common.expectedEndpoint')} <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-sm">GET /api/v1/public/master-models/{selectedMasterModel.master_model_id}/variants</code>
              </p>
            </div>
          </div>
        ) : modelVariants.length > 0 ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                {variantSortConfig && (
                  <button 
                    onClick={() => setVariantSortConfig(null)}
                    className="btn-secondary text-sm"
                  >
                    {t('common.resetSort')}
                  </button>
                )}
              </div>
              <ExportButtons
                data={sortedVariants}
                columns={variantColumns}
                metadata={{
                  title: t('exportTitles.brandVariants', { brand: selectedBrand, model: selectedMasterModel.master_model }),
                  dateRange: t('common.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) }),
                  filters: [
                    vehicleFilter === 'personbil' ? t('common.filterPersonbil') : 
                    vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
                    minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : ''
                  ].filter(f => f),
                }}
                disabled={variantsLoading}
              />
            </div>
            <div className="card overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('model_variant')}>
                    Variant {getVariantSortIcon('model_variant')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('total_count')}>
                    {t('common.registrations')} {getVariantSortIcon('total_count')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('market_share_percentage')}>
                    {t('common.marketShare')} {getVariantSortIcon('market_share_percentage')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('ev_percentage')}>
                    {t('common.evPct')} {getVariantSortIcon('ev_percentage')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('leasing_percentage')}>
                    Leasing % {getVariantSortIcon('leasing_percentage')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('company_percentage')}>
                    {t('common.companyPct')} {getVariantSortIcon('company_percentage')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedVariants.map((variant, index) => (
                  <tr key={index}>
                    <td className="font-semibold">{variant.model_variant}</td>
                    <td className="text-right">{variant.total_count.toLocaleString('nb-NO')}</td>
                    <td className="text-right text-primary-600 dark:text-primary-400">
                      {variant.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                    </td>
                    <td className="text-right text-green-600 dark:text-green-400">
                      {variant.ev_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                    </td>
                    <td className="text-right text-purple-600 dark:text-purple-400">
                      {variant.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                    </td>
                    <td className="text-right text-orange-600 dark:text-orange-400">
                      {(variant.company_sales_percentage + variant.company_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        ) : (
          <div className="card p-12 text-center">
            <Car className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {t('common.noVariantsFound')}
            </p>
          </div>
        )}
      </div>
    )
  }

  // Level 2: Master Models view (when brand is selected)
  if (selectedBrand) {
    return (
      <div className="space-y-6">
        {/* Header with back button */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-dark-card hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('brand.backToBrands')}</span>
          </button>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <span className="hover:text-primary-600 cursor-pointer" onClick={handleBackToList}>{t('brand.brands')}</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-900 dark:text-gray-100 font-medium">{selectedBrand}</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {t('brand.masterModelsTitle', { brand: selectedBrand })}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {t('brand.clickMasterModel')}
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        {masterModels.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <Car className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.masterModels')}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {masterModels.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.totalVehicles')}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {masterModels.reduce((sum, mm) => sum + mm.total_count, 0).toLocaleString('nb-NO')}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <Zap className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.ev100Models')}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {masterModels.filter(mm => mm.is_electric === 1).length}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.avgCompanyPct')}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {masterModels.length > 0
                      ? (
                          masterModels.reduce((sum, mm) => 
                            sum + mm.company_sales_percentage + mm.company_leasing_percentage, 0
                          ) / masterModels.length
                        ).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Master Models Table */}
        {masterModelsLoading ? (
          <LoadingSpinner />
        ) : masterModelsError ? (
          <ErrorMessage message={masterModelsError} />
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                {masterModelSortConfig && (
                  <button 
                    onClick={() => setMasterModelSortConfig(null)}
                    className="btn-secondary text-sm"
                  >
                    {t('common.resetSort')}
                  </button>
                )}
              </div>
              <ExportButtons
                data={sortedMasterModels}
                columns={masterModelColumns}
                metadata={{
                  title: t('exportTitles.brandMasterModels', { brand: selectedBrand }),
                  dateRange: t('common.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) }),
                  filters: [
                    vehicleFilter === 'personbil' ? t('common.filterPersonbil') : 
                    vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
                    minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : ''
                  ].filter(f => f),
                }}
                disabled={masterModelsLoading}
              />
            </div>
            <div className="card overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('ranking')} title={t('common.originalRanking')}>
                    # {getMasterModelSortIcon('ranking')}
                  </th>
                  <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('master_model')}>
                    {t('common.masterModel')} {getMasterModelSortIcon('master_model')}
                  </th>
                  <th className="text-center">Type</th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('total_count')}>
                    {t('common.registrations')} {getMasterModelSortIcon('total_count')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('market_share_percentage')}>
                    {t('common.marketShare')} {getMasterModelSortIcon('market_share_percentage')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('ev_percentage')}>
                    {t('common.evPct')} {getMasterModelSortIcon('ev_percentage')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('leasing_percentage')}>
                    Leasing % {getMasterModelSortIcon('leasing_percentage')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('company_total')}>
                    {t('common.companyTotal')} {getMasterModelSortIcon('company_total')}
                  </th>
                  <th className="text-center">{t('common.action')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Summary Row (TOTAL) for this brand's master models */}
                {masterModelsSummary && (
                  <tr className="bg-primary-50 dark:bg-primary-900/20 border-y-2 border-primary-200 dark:border-primary-800 sticky top-0 z-10">
                    <td className="text-center py-4">
                      <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                    </td>
                    <td className="font-bold text-base uppercase tracking-wide text-primary-700 dark:text-primary-300 py-4">
                      <div className="flex items-center gap-2">
                        TOTAL
                      </div>
                    </td>
                    <td className="text-center py-4">
                      <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                    </td>
                    <td className="text-right font-bold text-base py-4">{masterModelsSummary.total_count.toLocaleString('nb-NO')}</td>
                    <td className="text-right py-4">
                      <span className="font-bold text-base text-primary-600 dark:text-primary-400">
                        {masterModelsSummary.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                      </span>
                    </td>
                    <td className="text-right py-4">
                      <span className="font-bold text-base text-green-600 dark:text-green-400">
                        {masterModelsSummary.ev_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                      </span>
                    </td>
                    <td className="text-right py-4">
                      <span className="font-bold text-base text-purple-600 dark:text-purple-400">
                        {masterModelsSummary.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                      </span>
                    </td>
                    <td className="text-right py-4">
                      <span className="font-bold text-base text-orange-600 dark:text-orange-400">
                        {(masterModelsSummary.company_sales_count + masterModelsSummary.company_leasing_count).toLocaleString('nb-NO')}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">
                        ({(masterModelsSummary.company_sales_percentage + masterModelsSummary.company_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %)
                      </span>
                    </td>
                    <td className="text-center py-4">
                      <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                    </td>
                  </tr>
                )}
                
                {/* Master Model Rows */}
                {sortedMasterModels.map((mm) => (
                  <tr 
                    key={mm.master_model_id} 
                    className="hover:bg-gray-50 dark:hover:bg-dark-border cursor-pointer transition-colors"
                    onClick={() => handleMasterModelClick(mm)}
                  >
                    <td className="text-center text-gray-700 dark:text-gray-300">
                      {mm.ranking ?? '—'}
                    </td>
                    <td className="font-semibold">
                      <div className="flex items-center gap-2">
                        {mm.is_electric === 1 && (
                          <span title={t('common.electric100')}>
                            <Zap className="w-4 h-4 text-green-500" />
                          </span>
                        )}
                        {mm.master_model}
                      </div>
                    </td>
                    <td className="text-center">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {mm.body_type || '-'}
                      </span>
                    </td>
                    <td className="text-right font-medium">{mm.total_count.toLocaleString('nb-NO')}</td>
                    <td className="text-right">
                      <span className="text-primary-600 dark:text-primary-400">
                        {mm.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                      </span>
                    </td>
                    <td className="text-right">
                      <span className="text-green-600 dark:text-green-400">
                        {mm.ev_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                      </span>
                    </td>
                    <td className="text-right">
                      <span className="text-purple-600 dark:text-purple-400">
                        {mm.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                      </span>
                    </td>
                    <td className="text-right">
                      <span className="text-orange-600 dark:text-orange-400">
                        {getTotalCompanyVehicles(mm).toLocaleString('nb-NO')}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">
                        ({(mm.company_sales_percentage + mm.company_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %)
                      </span>
                    </td>
                    <td className="text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleMasterModelClick(mm)
                        }}
                        className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        <span className="text-sm font-medium">{t('common.seeVariants')}</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {masterModels.length === 0 && (
              <div className="p-12 text-center">
                <Car className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  {t('brand.noMasterModelsFor', { brand: selectedBrand })}
                </p>
              </div>
            )}
          </div>
          </div>
        )}
      </div>
    )
  }

  // Main brand list view (Level 1)
  // Get vehicle type label for page title
  const getVehicleTypeLabel = () => {
    switch (vehicleFilter) {
      case 'varebil':
        return t('vehicleTypes.varebil')
      case 'personbil':
        return t('vehicleTypes.personbil')
      case 'alle':
        return t('vehicleTypes.alle')
      default:
        return t('vehicleTypes.personbil')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('brand.title', { type: getVehicleTypeLabel() })}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('brand.subtitle')}
          </p>
        </div>
      </div>


      {/* Date Range Filter */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={handleOutputModeChange}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApplyFilters}
        bottomContent={
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        }
      />
      {dateRangeAlert && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t('common.errorPrefix')}</strong> {dateRangeAlert}
          </p>
        </div>
      )}

      {/* Period Info Banner */}
      <PeriodInfoBanner
        maxDataDate={maxDataDate}
        outputMode={outputMode}
        periodLabel={t(`outputModes.${outputMode}`)}
        startDate={startDate.toISOString().split('T')[0]}
        endDate={endDate.toISOString().split('T')[0]}
      />

      {/* Market Summary Card */}
      {brandSummary && (
        <div className="card p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {t('common.marketOverview')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-dark-border rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('common.totalRegistrations')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {brandSummary.total_count.toLocaleString('nb-NO')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('brand.showingTop', { n: brands.length })}
              </p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('common.evShare')}</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {brandSummary.ev_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('common.cars', { n: brandSummary.ev_count.toLocaleString('nb-NO') })}
              </p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('common.leasingShare')}</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {brandSummary.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('common.cars', { n: brandSummary.leasing_count.toLocaleString('nb-NO') })}
              </p>
            </div>
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('common.privateLeasing')}</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {brandSummary.private_leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('common.cars', { n: brandSummary.private_leasing_count.toLocaleString('nb-NO') })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          {t('brand.top20')}
        </h2>
        {brands.length > 0 && (
          <TopBrandsChart data={brands.slice(0, 20)} onBrandClick={handleBrandClick} />
        )}
      </div>

      {/* Search and Table */}
      <div className="card p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            {brandSortConfig && (
              <button 
                onClick={() => setBrandSortConfig(null)}
                className="btn-secondary text-sm"
              >
                {t('common.resetSort')}
              </button>
            )}
          </div>
          <ExportButtons
            data={sortedBrands}
            columns={brandColumns}
            metadata={{
              title: t('brand.exportTitle'),
              dateRange: t('common.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) }),
              filters: [
                vehicleFilter === 'personbil' ? t('common.filterPersonbil') : 
                vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
                minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : ''
              ].filter(f => f),
            }}
            disabled={loading}
          />
        </div>
        
        <div className="mb-4">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('common.searchBrand')}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('ranking')} title={t('common.originalRanking')}>
                  # {getSortIcon('ranking')}
                </th>
                <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('brand')}>
                  {t('common.brand')} {getSortIcon('brand')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('total_count')}>
                  {t('common.registrations')} {getSortIcon('total_count')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('market_share_percentage')}>
                  {t('common.marketShare')} {getSortIcon('market_share_percentage')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('avg_owner_age')}>
                  {t('common.avgOwnerAge')} {getSortIcon('avg_owner_age')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('leasing_percentage')}>
                  Leasing % {getSortIcon('leasing_percentage')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('company_sales_percentage')}>
                  {t('common.companySalesPct')} {getSortIcon('company_sales_percentage')}
                </th>
                <th className="text-center">{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {/* Summary Row (TOTAL) */}
              {brandSummary && (
                <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t-4 border-b-4 border-primary-400 dark:border-primary-600">
                  <td className="text-center py-4">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">∑</span>
                  </td>
                  <td className="font-bold text-base text-primary-700 dark:text-primary-300 py-4">TOTAL</td>
                  <td className="text-right font-bold text-base py-4">{brandSummary.total_count.toLocaleString('nb-NO')}</td>
                  <td className="text-right py-4">
                    <span className="font-bold text-base text-primary-600 dark:text-primary-400">
                      {brandSummary.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                    </span>
                  </td>
                  <td className="text-right font-bold text-base py-4">{brandSummary.avg_owner_age ? brandSummary.avg_owner_age.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) : '-'}</td>
                  <td className="text-right font-bold text-base py-4">{brandSummary.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %</td>
                  <td className="text-right font-bold text-base py-4">{brandSummary.company_sales_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %</td>
                  <td className="text-center py-4">
                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                  </td>
                </tr>
              )}
              
              {/* Brand Rows */}
              {sortedBrands.map((brand) => (
                <tr
                  key={brand.brand}
                  className={`hover:bg-gray-50 dark:hover:bg-dark-border cursor-pointer transition-colors ${activeMerker.has(brand.brand) ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500' : ''}`}
                  onClick={() => handleBrandClick(brand.brand)}
                >
                  <td className="text-center text-gray-700 dark:text-gray-300">
                    {brand.ranking ?? '—'}
                  </td>
                  <td className="font-medium">{brand.brand}</td>
                  <td className="text-right">{brand.total_count?.toLocaleString('nb-NO')}</td>
                  <td className="text-right">
                    <span className="text-primary-600 dark:text-primary-400">
                      {brand.market_share_percentage?.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                    </span>
                  </td>
                  <td className="text-right">{brand.avg_owner_age ? brand.avg_owner_age.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) : '-'}</td>
                  <td className="text-right">{brand.leasing_percentage?.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %</td>
                  <td className="text-right">{brand.company_sales_percentage?.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %</td>
                  <td className="text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleBrandClick(brand.brand)
                      }}
                      className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      <span className="text-sm font-medium">{t('brand.seeMasterModels')}</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {sortedBrands.length === 0 && (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            {t('brand.noBrandsFound')}
          </p>
        )}
      </div>
    </div>
  )
}
